import type { ProjectionType, StereoLayout } from "./vr-store"

export interface VRDetectionResult {
  projectionType: ProjectionType
  stereoLayout: StereoLayout
  confidence: "high" | "low"
}

// VRCA standard naming conventions + common studio patterns.
//
// These run against a SEPARATOR-NORMALISED name (see normaliseName): every run of
// `_`, `-`, `.` or whitespace becomes a single space. That normalisation is the whole
// point — the rules used to be written against raw underscores with `\b` boundaries,
// which silently never matched the most common real-world convention. In JS regex `_`
// is a word character, so in "42562_LR_180" there is no word boundary before "LR" or
// before "180", and both `\b_lr\b` and `\b180\b` failed. Every underscore-delimited
// VR filename was therefore reported as "not VR".
const DETECTION_RULES: Array<{
  pattern: RegExp
  projection: ProjectionType
  stereo: StereoLayout
  confidence: "high" | "low"
}> = [
  // MKX200/220 — fisheye lenses used by major studios
  { pattern: /mkx200/i, projection: "mkx200", stereo: "sbs", confidence: "high" },
  { pattern: /mkx220/i, projection: "mkx220", stereo: "sbs", confidence: "high" },
  { pattern: /vrca220/i, projection: "mkx220", stereo: "sbs", confidence: "high" },
  { pattern: /vrca200/i, projection: "mkx200", stereo: "sbs", confidence: "high" },

  // Fisheye generic
  { pattern: /fisheye/i, projection: "fisheye_180", stereo: "sbs", confidence: "high" },
  { pattern: /\bfish\b/i, projection: "fisheye_180", stereo: "sbs", confidence: "high" },

  // 360 stereo
  { pattern: /\b360\b.*\btb\b/i, projection: "equirectangular_360", stereo: "tb", confidence: "high" },
  { pattern: /\b360\b.*\bsbs\b/i, projection: "equirectangular_360", stereo: "sbs", confidence: "high" },
  { pattern: /\b360\b.*\blr\b/i, projection: "equirectangular_360", stereo: "sbs", confidence: "high" },
  { pattern: /\b360 3dh\b/i, projection: "equirectangular_360", stereo: "sbs", confidence: "high" },
  { pattern: /\b360 3dv\b/i, projection: "equirectangular_360", stereo: "tb", confidence: "high" },

  // 180 stereo explicit
  { pattern: /\b180\b.*\bsbs\b/i, projection: "equirectangular_180", stereo: "sbs", confidence: "high" },
  { pattern: /\b180\b.*\btb\b/i, projection: "equirectangular_180", stereo: "tb", confidence: "high" },
  { pattern: /\b180\b.*\blr\b/i, projection: "equirectangular_180", stereo: "sbs", confidence: "high" },
  { pattern: /\b180 3dh\b/i, projection: "equirectangular_180", stereo: "sbs", confidence: "high" },
  { pattern: /\b180 3dv\b/i, projection: "equirectangular_180", stereo: "tb", confidence: "high" },

  // Stereo marker before the projection number (e.g. "..._LR_180") — same meaning,
  // just the other word order, which the rules above can't express.
  { pattern: /\bsbs\b.*\b180\b/i, projection: "equirectangular_180", stereo: "sbs", confidence: "high" },
  { pattern: /\blr\b.*\b180\b/i, projection: "equirectangular_180", stereo: "sbs", confidence: "high" },
  { pattern: /\btb\b.*\b180\b/i, projection: "equirectangular_180", stereo: "tb", confidence: "high" },

  // SBS / LR / TB patterns (assume 180 if not otherwise tagged — most common in VR adult)
  { pattern: /\bsbs\b/i, projection: "equirectangular_180", stereo: "sbs", confidence: "low" },
  { pattern: /\blr\b/i, projection: "equirectangular_180", stereo: "sbs", confidence: "low" },
  { pattern: /\bside by side\b/i, projection: "equirectangular_180", stereo: "sbs", confidence: "low" },
  { pattern: /\btb\b/i, projection: "equirectangular_180", stereo: "tb", confidence: "low" },
  { pattern: /\btop bottom\b/i, projection: "equirectangular_180", stereo: "tb", confidence: "low" },

  // 360 mono (just the number with no stereo indicator)
  { pattern: /\b360\b/i, projection: "equirectangular_360", stereo: "mono", confidence: "low" },

  // 180 VR is usually stereo SBS even when the filename omits an explicit SBS/LR tag.
  { pattern: /\b180\b/i, projection: "equirectangular_180", stereo: "sbs", confidence: "low" },

  // Known studio prefixes that always use MKX200/SBS
  { pattern: /^(povr|vrbgf|wankzvr|vrstars|czechvr|vrconk|badoinkvr|naughtyamerica)/i, projection: "mkx200", stereo: "sbs", confidence: "high" },
]

/** Collapses `_`, `-`, `.` and whitespace runs to single spaces so `\b` boundaries work. */
function normaliseName(name: string): string {
  return name.replace(/[._\-\s]+/g, " ").trim()
}

export function detectVRFromFilename(filename: string): VRDetectionResult | null {
  const name = normaliseName(filename.replace(/\.[^.]+$/, "")) // strip extension

  for (const rule of DETECTION_RULES) {
    if (rule.pattern.test(name)) {
      return {
        projectionType: rule.projection,
        stereoLayout: rule.stereo,
        confidence: rule.confidence,
      }
    }
  }

  return null
}

export function detectVRFromUrl(url: string): VRDetectionResult | null {
  try {
    const pathname = new URL(url).pathname
    const filename = pathname.split("/").pop() || ""
    return detectVRFromFilename(filename)
  } catch {
    return detectVRFromFilename(url)
  }
}

// Generate a stable key for per-video settings storage
export function getVideoSettingsKey(urlOrFilename: string): string {
  let str = urlOrFilename
  try {
    const url = new URL(urlOrFilename)
    str = url.pathname + url.hostname
  } catch {
    // local filename, use as-is
  }

  // Simple djb2 hash
  let hash = 5381
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i)
  }
  return (hash >>> 0).toString(36)
}

const VR_TAGS = new Set([
  "vr", "virtual_reality", "360", "360_video", "360°", "180", "stereoscopic",
  "pov_vr", "3d_vr", "oculus", "quest", "vr_video", "immersive",
])

const VIDEO_EXT = /\.(webm|mp4|mov|avi|mkv|flv|wmv|m4v|3gp|ogv)(\?|$)/i

/**
 * Sources that publish VR/AR content exclusively, so every video from them is VR
 * regardless of what its tags or file URL look like. Needed because several of these
 * carry no usable signal at all: sexvr posts ship with empty tags and a file URL of
 * `download.php?id=…&res=…`, which has nothing to pattern-match on.
 * Keep in sync with the VR entries in lib/source-store.ts.
 */
const VR_SITES = new Set(["eporner-vr", "vrpornbase", "arfreaks", "sexvr", "vrhump"])

function normalizeTags(tags: unknown): string[] {
  if (Array.isArray(tags)) {
    return tags
      .map((tag) => {
        if (typeof tag === "string") return tag
        if (tag && typeof tag === "object" && "name" in tag) return String((tag as { name?: unknown }).name ?? "")
        return ""
      })
      .filter(Boolean)
  }
  if (typeof tags === "string") {
    return tags.split(/\s+/).filter(Boolean)
  }
  return []
}

/** Returns true if the post looks like VR video content. Never true for albums or image posts. */
export function isVRPost(post: {
  type?: string
  fileUrl?: string
  sampleUrl?: string
  tags?: unknown
  site?: string
}): boolean {
  // Albums are never VR
  if (post.type === "album") return false

  // Must be a video
  const url = post.fileUrl ?? post.sampleUrl ?? ""
  const isVideo = post.type === "video" || VIDEO_EXT.test(url)
  if (!isVideo) return false

  // VR-only source — authoritative, and the only signal some of them give us.
  if (post.site && VR_SITES.has(post.site.toLowerCase())) return true

  // Tag check (fast path). Tags are matched whole AND split on separators, because real
  // tags arrive as phrases — "180 sbs" and "vr pornnow" both carry a VR token that an
  // exact whole-string lookup against VR_TAGS would miss.
  const tagTokens = normalizeTags(post.tags).flatMap((t) => {
    const lower = t.toLowerCase()
    return [lower, ...lower.split(/[\s_\-]+/)]
  })
  if (tagTokens.some((t) => VR_TAGS.has(t))) return true

  // Filename/URL convention check
  if (url && detectVRFromUrl(url)) return true

  return false
}

export function projectionLabel(type: ProjectionType): string {
  switch (type) {
    case "equirectangular_360": return "360° Equirectangular"
    case "equirectangular_180": return "180° Equirectangular"
    case "fisheye_180": return "180° Fisheye"
    case "mkx200": return "MKX200 Fisheye"
    case "mkx220": return "MKX220 Fisheye"
    case "flat": return "Flat (2D)"
  }
}

export function stereoLabel(layout: StereoLayout): string {
  switch (layout) {
    case "mono": return "Mono source"
    case "sbs": return "Side-by-side source"
    case "tb": return "Top/bottom source"
  }
}
